import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Mail, Phone, MapPin, Clock, Send, MessageSquare, CheckCircle } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const Contacto = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    category: "general"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/tickets`, formData);
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success("Ticket enviado exitosamente");
    } catch (error) {
      toast.error("Error al enviar el ticket");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "WhatsApp",
      value: "+52 55 6334 1691",
      description: "Ventas, cotizaciones y soporte",
      link: "https://wa.me/525563341691?text=Hola%20SuperPack%2C%20necesito%20ayuda"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      value: "soporte@superpack.mx",
      description: "Respondemos en menos de 24 horas"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Oficina Central",
      value: "Av. Santa Fe 417, Of. 14",
      description: "Ciudad de México, CDMX"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Horario",
      value: "Lunes a Viernes",
      description: "9:00 AM - 6:00 PM (Hora Centro)"
    },
  ];

  const categories = [
    { value: "general", label: "Consulta general" },
    { value: "soporte", label: "Soporte técnico" },
    { value: "facturacion", label: "Facturación" },
    { value: "sobrepeso", label: "Disputa de sobrepeso" },
    { value: "ventas", label: "Ventas corporativas" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-lg border-b border-zinc-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Package className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-['Outfit']">
                <span className="text-violet-600">Super</span>
                <span className="text-zinc-800">Pack</span>
              </span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="/" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Inicio</a>
              <a href="/sobre-nosotros" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Sobre Nosotros</a>
              <a href="/contacto" className="text-violet-600 font-medium">Contacto</a>
            </nav>

            <Button 
              onClick={() => navigate(user ? "/dashboard" : "/register")}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6"
            >
              {user ? "Mi Dashboard" : "Comenzar"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-violet-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] mb-4">
            ¿Cómo podemos ayudarte?
          </h1>
          <p className="text-violet-100 text-lg max-w-2xl mx-auto">
            Nuestro equipo está listo para resolver tus dudas y brindarte el mejor soporte.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Wrapper = info.link ? "a" : "div";
              const wrapperProps = info.link ? { href: info.link, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Wrapper key={index} {...wrapperProps}>
                  <Card className={`bg-white shadow-xl hover:shadow-2xl transition-shadow h-full ${info.link ? "cursor-pointer hover:border-violet-300" : ""}`}>
                    <CardContent className="p-6 text-center">
                      <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center text-violet-600 mx-auto mb-4">
                        {info.icon}
                      </div>
                      <h3 className="font-bold text-lg mb-1">{info.title}</h3>
                      <p className="text-violet-600 font-medium">{info.value}</p>
                      <p className="text-zinc-500 text-sm mt-1">{info.description}</p>
                    </CardContent>
                  </Card>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <MessageSquare className="w-8 h-8" />
              </div>
              <CardTitle className="text-2xl font-['Outfit']">Envíanos un ticket</CardTitle>
              <p className="text-zinc-500">Te responderemos lo antes posible</p>
            </CardHeader>

            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">¡Ticket enviado!</h3>
                  <p className="text-zinc-600 mb-4">
                    Tu número de ticket es: <strong className="text-violet-600">{ticketId}</strong>
                  </p>
                  <p className="text-zinc-500 mb-6">
                    Te contactaremos pronto. {user ? "Puedes ver el estado de tu ticket en tu dashboard." : ""}
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => navigate("/")} variant="outline">
                      Volver al inicio
                    </Button>
                    {user && (
                      <Button onClick={() => navigate("/dashboard/tickets")} className="bg-violet-600">
                        Ver mis tickets
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="category">Categoría</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Asunto</Label>
                    <Input
                      id="subject"
                      placeholder="¿En qué podemos ayudarte?"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Mensaje</Label>
                    <Textarea
                      id="message"
                      placeholder="Describe tu consulta con el mayor detalle posible..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={6}
                      className="mt-2"
                    />
                  </div>

                  {user && (
                    <div className="bg-violet-50 p-4 rounded-lg">
                      <p className="text-sm text-violet-700">
                        Enviando como: <strong>{user.name}</strong> ({user.email})
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 py-6 text-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </div>
                    ) : (
                      <>
                        Enviar ticket <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold font-['Outfit'] mb-8">Preguntas frecuentes</h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {[
              { q: "¿Cuánto tarda en llegar mi envío?", a: "Depende del servicio elegido. Express: 1-2 días, Terrestre: 3-7 días." },
              { q: "¿Cómo puedo rastrear mi paquete?", a: "Desde tu dashboard puedes ver el tracking en tiempo real de todos tus envíos." },
              { q: "¿Qué hago si mi paquete tiene sobrepeso?", a: "Recibirás una notificación y podrás pagar o disputar el cargo desde tu cuenta." },
              { q: "¿Puedo facturar mis envíos?", a: "Sí, todos los envíos son facturables. Solicita tu factura desde el dashboard." },
            ].map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="font-bold mb-2">{faq.q}</h3>
                  <p className="text-zinc-500 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-400 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-bold font-['Outfit'] text-xl">SuperPack</span>
          </div>
          <p className="text-sm">© 2026 SuperPack.mx - Todos los derechos reservados</p>
        </div>
      </footer>
    </div>
  );
};

export default Contacto;
